'use client';

import { useEffect, useRef, useState, useCallback } from 'react';

// ─── Типы ────────────────────────────────────────────────────────────────────
type GameState = 'menu' | 'playing' | 'over';

// Тело нейрона (сома) с бонусами внутри
type Junction = {
  x: number; y: number; r: number;
  bonuses: { dx: number; dy: number; kind: 'data' | 'weight'; collected: boolean }[];
  hasEnergy: boolean;
  energyTaken: boolean;
};

// Путь: аксон → синаптическая щель → дендрит
type Pathway = {
  fromIdx: number;
  toIdx: number;
  fromY: number;
  toY: number;
  fromX: number;
  toX: number;
  cpY: number;
  hw: number;
  // Синаптический затвор: 0 = открыт, 1-3 = стоимость энергии
  gateCost: number;
  gateOpen: boolean;
  // Бонусы за очки (ромбы/круги)
  dataT: number;
  dataKind: 'data' | 'weight';
  collected: boolean;
  openedAt: number; // timestamp активации синапса (для вспышки)
};

type Particle = { x: number; y: number; vx: number; vy: number; life: number; color: string; r: number };

// ─── Стадии ─────────────────────────────────────────────────────────────────
const STAGES = [
  { name: 'Перцептрон', threshold: 0, accent: '#22d3ee', spacing: 270, paths: 2, maxCost: 2, energyChance: 0.75, synapseHW: 22 },
  { name: 'Глубокое обучение', threshold: 500, accent: '#818cf8', spacing: 240, paths: 3, maxCost: 2, energyChance: 0.65, synapseHW: 20 },
  { name: 'Трансформер', threshold: 2000, accent: '#c084fc', spacing: 210, paths: 4, maxCost: 3, energyChance: 0.55, synapseHW: 18 },
  { name: 'Сверхинтеллект', threshold: 5000, accent: '#f0abfc', spacing: 185, paths: 5, maxCost: 3, energyChance: 0.45, synapseHW: 16 },
] as const;
 
const PLAYER_R = 10;
const BASE_SPEED = 1 ;
const JUNCTION_R = 30 ;
const TRAIL_LEN = 12;
const BEZIER_SAMPLES = 18;
const MIN_JUNC_SEP = 75;
const MAX_NET_SPREAD = 420;
const MAX_ENERGY = 4;
// Цвет индикатора энергии: 0=серый, 1=зелёный, 2=синий, 3=оранжевый, 4=красный
const ENERGY_COLORS = ['#6b7280', '#22c55e', '#3b82f6', '#f97316', '#ef4444'];
// Цвета затвора по стоимости: 1=жёлтый, 2=оранжевый, 3=красный
const GATE_COLORS = ['', '#facc15', '#f97316', '#ef4444'];
const SYNAPSE_GAP = 31; // Ширина синаптической щели в пикселях (+20%)

// ─── Пропсы ─────────────────────────────────────────────────────────────────
interface Props {
  onExit?: () => void;
  onScoreSubmit?: (score: number) => Promise<void>;
  leaderboard?: { name: string; score: number; avatar?: string | null }[];
  myBest?: number;
  globalBest?: number;
}

// ─── Утилиты ────────────────────────────────────────────────────────────────
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const qBez = (a: number, cp: number, b: number, t: number) => (1 - t) * (1 - t) * a + 2 * (1 - t) * t * cp + t * t * b;

// Биологический профиль ширины: толсто у нейрона, тонко посередине, бугорок у синапса
const hwAt = (baseHW: number, t: number) => {
  const d = 1 - 2 * Math.abs(t - 0.5);
  const shaft = 1 - 0.5 * d;
  const synBulge = 0.18 * Math.exp(-(((t - 0.5) * 7) ** 2));
  return Math.max(PLAYER_R + 3, baseHW * (shaft + synBulge));
};

const hexRgb = (hex: string) => { const h = hex.replace('#', ''); return `${parseInt(h.substring(0, 2), 16)},${parseInt(h.substring(2, 4), 16)},${parseInt(h.substring(4, 6), 16)}`; };

// Бонусы внутри тела нейрона (за очки)
const makeBonuses = () => {
  const n = Math.random() < 0.3 ? 0 : Math.random() < 0.6 ? 1 : 2;
  const out: Junction['bonuses'] = [];
  for (let i = 0; i < n; i++) {
    const a = Math.random() * Math.PI * 2;
    const dist = 6 + Math.random() * (JUNCTION_R - 14);
    out.push({ dx: Math.cos(a) * dist, dy: Math.sin(a) * dist, kind: Math.random() < 0.2 ? 'weight' : 'data', collected: false });
  }
  return out;
};

export default function NeuralRunner({ onExit, onScoreSubmit, leaderboard = [], myBest = 0, globalBest = 0 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [gameState, setGameState] = useState<GameState>('menu');
  const [displayScore, setDisplayScore] = useState(0);
  const [displayStage, setDisplayStage] = useState(0);
  const [displayEnergy, setDisplayEnergy] = useState(0);
  const [finalScore, setFinalScore] = useState(0);
  const [bestScore, setBestScore] = useState(myBest);
  const rafRef = useRef(0);

  const g = useRef({
    state: 'menu' as GameState,
    camX: 0, speed: BASE_SPEED,
    score: 0, bonus: 0, startAt: 0, stageIdx: 0,
    energy: 0, // 0-4 уровня энергии сигнала
    playerY: 0, targetY: 0, playerWorldX: 0,
    currentPathway: -1, atJunction: -1,
    trail: [] as { x: number; y: number }[],
    junctions: [] as Junction[],
    pathways: [] as Pathway[],
    frontier: [] as number[],
    particles: [] as Particle[],
    w: 800, h: 600,
  });

  const inputRef = useRef({ pointerY: -1, keysUp: false, keysDown: false, active: false });

  const pathYAt = (p: Pathway, t: number) => qBez(p.fromY, p.cpY, p.toY, t);
  const pathXAt = (p: Pathway, t: number) => lerp(p.fromX, p.toX, t);
  const pathProgress = (p: Pathway, worldX: number) => clamp((worldX - p.fromX) / (p.toX - p.fromX || 1), 0, 1);

  // ─── Создание пути между нейронами ────────────────────────────────────────
  const addSynapse = (fromIdx: number, toIdx: number, gateCost: number) => {
    const s = g.current;
    const st = STAGES[s.stageIdx];
    const fromJ = s.junctions[fromIdx];
    const toJ = s.junctions[toIdx];

    // Контрольная точка: все кривые с видимым изгибом
    const midY = (fromJ.y + toJ.y) / 2;
    const rng = Math.random();
    const dir = Math.random() < 0.5 ? 1 : -1;
    let cpY: number;
    if (rng < 0.3) {
      cpY = midY + dir * (55 + Math.random() * 50);
    } else if (rng < 0.75) {
      cpY = midY + dir * (22 + Math.random() * 35);
    } else {
      cpY = midY + dir * (12 + Math.random() * 15);
    }
    cpY = clamp(cpY, 15, s.h - 15);

    // Бонусы за очки (вне зоны синапса)
    let dataT = -1;
    let dataKind: 'data' | 'weight' = 'data';
    if (Math.random() < 0.35) {
      dataT = Math.random() < 0.5 ? (0.12 + Math.random() * 0.28) : (0.6 + Math.random() * 0.28);
      dataKind = Math.random() < 0.2 ? 'weight' : 'data';
    }

    s.pathways.push({
      fromIdx, toIdx,
      fromY: fromJ.y, toY: toJ.y, fromX: fromJ.x, toX: toJ.x,
      cpY, hw: Math.round(st.synapseHW * (0.85 + Math.random() * 0.3)),
      gateCost, gateOpen: false,
      dataT, dataKind, collected: false,
      openedAt: 0,
    });
  };

  // ─── Послойная генерация нейросети ─────────────────────────────────────────
  const generateLayer = (prevIndices: number[], layerX: number): number[] => {
    const s = g.current;
    const st = STAGES[s.stageIdx];
    const margin = 60;

    // Число нейронов в слое
    const rng = Math.random();
    const numNew = rng < 0.15 ? Math.max(2, st.paths - 1) : rng < 0.7 ? st.paths : Math.min(st.paths + 1, 6);

    // Центр слоя дрейфует от предыдущего
    const prevYs = prevIndices.map(i => s.junctions[i].y);
    const prevCenter = prevYs.reduce((a, b) => a + b, 0) / prevYs.length;
    const layerCenter = clamp(prevCenter + (Math.random() - 0.5) * 50, margin + MAX_NET_SPREAD / 2, s.h - margin - MAX_NET_SPREAD / 2);
    const halfSpread = Math.min(MAX_NET_SPREAD / 2, (s.h - margin * 2) / 2);
    const yMin = Math.max(margin, layerCenter - halfSpread);
    const yMax = Math.min(s.h - margin, layerCenter + halfSpread);

    const yPos: number[] = [];
    for (let i = 0; i < numNew; i++) {
      let y = 0, ok = false;
      for (let att = 0; att < 100; att++) {
        y = yMin + Math.random() * (yMax - yMin);
        if (yPos.every(py => Math.abs(py - y) >= MIN_JUNC_SEP)) { ok = true; break; }
      }
      if (!ok) y = yMin + ((yMax - yMin) / (numNew + 1)) * (i + 1);
      yPos.push(y);
    }
    yPos.sort((a, b) => a - b);

    const newIndices: number[] = [];
    for (const y of yPos) {
      newIndices.push(s.junctions.length);
      s.junctions.push({
        x: layerX, y, r: JUNCTION_R, bonuses: makeBonuses(),
        hasEnergy: Math.random() < st.energyChance, energyTaken: false,
      });
    }

    // ── Соединения: 1 аксон (макс 2 ветви), много дендритов ──
    const used = new Set<string>();
    const outCount = new Map<number, number>();
    const inCount = new Map<number, number>();

    const connect = (pi: number, ni: number, cost: number) => {
      const k = `${pi}-${ni}`;
      if (used.has(k)) return;
      addSynapse(pi, ni, Math.min(cost, 3));
      used.add(k);
      outCount.set(pi, (outCount.get(pi) || 0) + 1);
      inCount.set(ni, (inCount.get(ni) || 0) + 1);
    };

    // 1) Каждый prev → ближайший new (стоимость 1 — гарантированный проход)
    for (const pi of prevIndices) {
      const py = s.junctions[pi].y;
      let best = newIndices[0], bestD = Infinity;
      for (const ni of newIndices) { const d = Math.abs(s.junctions[ni].y - py); if (d < bestD) { bestD = d; best = ni; } }
      connect(pi, best, 1);
    }

    // 2) Каждый new без входа ← ближайший prev (стоимость 1)
    for (const ni of newIndices) {
      if ((inCount.get(ni) || 0) >= 1) continue;
      const ny = s.junctions[ni].y;
      let best = prevIndices[0], bestD = Infinity;
      for (const pi of prevIndices) { const d = Math.abs(s.junctions[pi].y - ny); if (d < bestD) { bestD = d; best = pi; } }
      connect(best, ni, 1 + Math.floor(Math.random() * st.maxCost));
    }

    // 3) Доп. ветвления аксонов (макс 2 выхода, стоимость 1-maxCost)
    for (const pi of prevIndices) {
      if ((outCount.get(pi) || 0) >= 2) continue;
      if (Math.random() < 0.55) {
        const ni = newIndices[Math.floor(Math.random() * newIndices.length)];
        connect(pi, ni, 1 + Math.floor(Math.random() * st.maxCost));
      }
    }

    return newIndices;
  };

  const ensureNetwork = () => {
    const s = g.current;
    const targetX = s.camX + s.w + 500;
    if (s.frontier.length === 0) return;
    let maxX = Math.max(...s.frontier.map(i => s.junctions[i].x));
    while (maxX < targetX) {
      const nextX = maxX + STAGES[s.stageIdx].spacing;
      s.frontier = generateLayer(s.frontier, nextX);
      maxX = nextX;
    }
  };

  // ─── Инициализация ────────────────────────────────────────────────────────
  const initGame = useCallback(() => {
    const s = g.current;
    s.state = 'playing';
    s.camX = -(s.w * 0.22); s.speed = BASE_SPEED;
    s.score = 0; s.bonus = 0; s.energy = 2;
    s.startAt = performance.now(); s.stageIdx = 0;
    s.trail = []; s.particles = [];
    s.junctions = [{ x: 0, y: s.h / 2, r: JUNCTION_R, bonuses: [], hasEnergy: false, energyTaken: false }];
    s.pathways = [];
    s.frontier = [0];
    s.playerY = s.h / 2; s.targetY = s.h / 2;
    s.playerWorldX = 0; s.atJunction = 0; s.currentPathway = -1;
    ensureNetwork();
    inputRef.current = { pointerY: -1, keysUp: false, keysDown: false, active: false };
    setGameState('playing');
    setDisplayScore(0);
    setDisplayStage(0);
    setDisplayEnergy(2);
  }, []);

  const endGame = useCallback((score: number) => {
    g.current.state = 'over';
    setFinalScore(score);
    setBestScore(b => Math.max(b, score));
    setGameState('over');
    onScoreSubmit?.(score);
  }, [onScoreSubmit]);

  // ─── Canvas resize ────────────────────────────────────────────────────────
  useEffect(() => {
    const c = containerRef.current, cv = canvasRef.current;
    if (!c || !cv) return;
    const resize = () => {
      const r = c.getBoundingClientRect();
      const dpr = Math.min(2, devicePixelRatio || 1);
      cv.width = r.width * dpr; cv.height = r.height * dpr;
      cv.style.width = `${r.width}px`; cv.style.height = `${r.height}px`;
      cv.getContext('2d')?.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.current.w = r.width; g.current.h = r.height;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(c);
    return () => ro.disconnect();
  }, []);

  // ─── Клавиатура ───────────────────────────────────────────────────────────
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const d = e.type === 'keydown';
      if (e.key === 'ArrowUp' || e.key === 'w' || e.key === 'W') inputRef.current.keysUp = d;
      if (e.key === 'ArrowDown' || e.key === 's' || e.key === 'S') inputRef.current.keysDown = d;
      if (e.key === ' ' && d && g.current.state !== 'playing') initGame();
    };
    addEventListener('keydown', h); addEventListener('keyup', h);
    return () => { removeEventListener('keydown', h); removeEventListener('keyup', h); };
  }, [initGame]);

  // ─── Указатель (мышь + тач) ──────────────────────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const gY = (e: PointerEvent) => e.clientY - el.getBoundingClientRect().top;
    const dn = (e: PointerEvent) => { if (g.current.state !== 'playing') return; e.preventDefault(); (e.target as HTMLElement).setPointerCapture?.(e.pointerId); inputRef.current.pointerY = gY(e); inputRef.current.active = true; };
    const mv = (e: PointerEvent) => { if (inputRef.current.active) inputRef.current.pointerY = gY(e); };
    const up = (e: PointerEvent) => { inputRef.current.active = false; (e.target as HTMLElement).releasePointerCapture?.(e.pointerId); };
    el.addEventListener('pointerdown', dn, { passive: false });
    el.addEventListener('pointermove', mv, { passive: true });
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    return () => { el.removeEventListener('pointerdown', dn); el.removeEventListener('pointermove', mv); el.removeEventListener('pointerup', up); el.removeEventListener('pointercancel', up); };
  }, []);

  useEffect(() => {
    if (gameState !== 'playing') return;
    const el = containerRef.current; if (!el) return;
    const p = (e: TouchEvent) => e.preventDefault();
    el.addEventListener('touchmove', p, { passive: false });
    return () => el.removeEventListener('touchmove', p);
  }, [gameState]);

  // ─── Рендер пути с синаптической щелью ──────────────────────────────────────
  const drawPathway = (ctx: CanvasRenderingContext2D, p: Pathway, camX: number, accent: string, t: number) => {
    const sx = p.fromX - camX, ex = p.toX - camX;
    if (ex < -60 || sx > g.current.w + 60) return;
    const rgb = hexRgb(accent);
    const len = Math.max(1, ex - sx);

    // Границы синаптической щели (t-space)
    const gH = Math.min(0.18, SYNAPSE_GAP / 2 / len);
    const t1 = 0.5 - gH, t2 = 0.5 + gH;

    // ── Трубка: широко у нейрона → тонко у синапса, обрезана по краю нейрона ──
    const tTrim = JUNCTION_R / Math.max(1, p.toX - p.fromX);
    const vhw = (tt: number) => {
      const d = 2 * Math.abs(tt - 0.5); // 0 у синапса, 1 у нейрона
      return PLAYER_R + 0.5 + d * d * 4;
    };
    for (const [tA, tB] of [[tTrim, t1], [t2, 1 - tTrim]] as [number, number][]) {
      if (tB <= tA) continue;
      const n = Math.max(6, Math.ceil(BEZIER_SAMPLES * (tB - tA)));
      ctx.beginPath();
      for (let i = 0; i <= n; i++) {
        const tt = tA + (tB - tA) * i / n;
        const x = lerp(sx, ex, tt), y = qBez(p.fromY, p.cpY, p.toY, tt) - vhw(tt);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      for (let i = n; i >= 0; i--) {
        const tt = tA + (tB - tA) * i / n;
        ctx.lineTo(lerp(sx, ex, tt), qBez(p.fromY, p.cpY, p.toY, tt) + vhw(tt));
      }
      ctx.closePath();
      ctx.fillStyle = `rgba(${rgb},0.22)`;
      ctx.fill();
      ctx.lineWidth = 1; ctx.strokeStyle = `rgba(${rgb},0.55)`;
      for (const side of [-1, 1]) {
        ctx.beginPath();
        for (let i = 0; i <= n; i++) {
          const tt = tA + (tB - tA) * i / n;
          const x = lerp(sx, ex, tt), y = qBez(p.fromY, p.cpY, p.toY, tt) + side * vhw(tt);
          i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
    }

    // ── Синаптическая щель (+20% масштаб) ──
    const synX = lerp(sx, ex, 0.5), synY = qBez(p.fromY, p.cpY, p.toY, 0.5);
    const synHW = hwAt(p.hw, 0.5) * 1.2;
    const preX = lerp(sx, ex, t1), preY = qBez(p.fromY, p.cpY, p.toY, t1);
    const postX = lerp(sx, ex, t2), postY = qBez(p.fromY, p.cpY, p.toY, t2);
    const gColor = GATE_COLORS[p.gateCost] || '#facc15';
    const gRgb = hexRgb(gColor);

    // Вспышка при недавней активации синапса
    if (p.gateOpen && p.openedAt > 0) {
      const flash = Math.max(0, 1 - (t - p.openedAt) / 600);
      if (flash > 0) {
        const fGl = ctx.createRadialGradient(synX, synY, 0, synX, synY, synHW + 16);
        fGl.addColorStop(0, `rgba(${gRgb},${(0.5 * flash).toFixed(2)})`);
        fGl.addColorStop(1, `rgba(${gRgb},0)`);
        ctx.beginPath(); ctx.arc(synX, synY, synHW + 16, 0, Math.PI * 2);
        ctx.fillStyle = fGl; ctx.fill();
      }
    }

    if (!p.gateOpen) {
      // ── Закрытый синапс ──
      // Пре-синаптический бутон (полуовал с везикулами)
      ctx.beginPath();
      ctx.ellipse(preX + 4, preY, 8, synHW * 0.6, 0, -Math.PI / 2, Math.PI / 2);
      ctx.fillStyle = `rgba(${gRgb},0.13)`; ctx.fill();
      ctx.strokeStyle = `rgba(${gRgb},0.5)`; ctx.lineWidth = 1.4; ctx.stroke();

      // Везикулы внутри бутона (число = стоимость энергии)
      for (let v = 0; v < p.gateCost; v++) {
        const vy = preY + (v - (p.gateCost - 1) / 2) * 7;
        ctx.beginPath(); ctx.arc(preX + 5, vy, 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${gRgb},0.6)`; ctx.fill();
      }

      // Пост-синаптическая мембрана (вогнутая дуга с рецепторами)
      ctx.beginPath();
      ctx.moveTo(postX, postY - synHW * 0.6);
      ctx.quadraticCurveTo(postX - 5, postY, postX, postY + synHW * 0.6);
      ctx.strokeStyle = `rgba(${rgb},0.4)`; ctx.lineWidth = 1.8; ctx.stroke();

      // Рецепторы ◁ на мембране
      for (let r = -1; r <= 1; r++) {
        const ry = postY + r * synHW * 0.28;
        ctx.beginPath();
        ctx.moveTo(postX, ry - 3); ctx.lineTo(postX - 4, ry); ctx.lineTo(postX, ry + 3);
        ctx.strokeStyle = `rgba(${rgb},0.3)`; ctx.lineWidth = 1; ctx.stroke();
      }

      // Число стоимости в щели
      ctx.fillStyle = `rgba(${gRgb},0.9)`;
      ctx.font = 'bold 13px monospace'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(String(p.gateCost), synX, synY);
    } else {
      // ── Открытый синапс — нейромедиаторы в щели ──
      const gl = ctx.createRadialGradient(synX, synY, 2, synX, synY, synHW);
      gl.addColorStop(0, `rgba(${rgb},0.12)`); gl.addColorStop(1, `rgba(${rgb},0)`);
      ctx.beginPath(); ctx.arc(synX, synY, synHW, 0, Math.PI * 2); ctx.fillStyle = gl; ctx.fill();

      // Тонкие мембраны
      ctx.strokeStyle = `rgba(${rgb},0.22)`; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(preX, preY - synHW * 0.5); ctx.lineTo(preX, preY + synHW * 0.5); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(postX, postY - synHW * 0.5); ctx.lineTo(postX, postY + synHW * 0.5); ctx.stroke();

      // Плавающие нейромедиаторы (анимация)
      const ph = t * 0.003;
      for (let d = 0; d < 4; d++) {
        const nx = preX + (postX - preX) * ((d + 0.5) / 4) + Math.sin(ph + d * 1.5) * 2;
        const ny = synY + Math.cos(ph * 1.2 + d * 2.1) * synHW * 0.3;
        ctx.beginPath(); ctx.arc(nx, ny, 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${rgb},0.35)`; ctx.fill();
      }
    }

    // ── Бонусы за очки ──
    if (p.dataT >= 0 && !p.collected) {
      const dx = lerp(sx, ex, p.dataT), dy = qBez(p.fromY, p.cpY, p.toY, p.dataT);
      ctx.save();
      if (p.dataKind === 'data') {
        ctx.translate(dx, dy); ctx.rotate(Math.PI / 4);
        ctx.fillStyle = 'rgba(74,222,128,0.85)'; ctx.fillRect(-5, -5, 10, 10);
      } else {
        ctx.beginPath(); ctx.arc(dx, dy, 6, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(96,165,250,0.85)'; ctx.fill();
      }
      ctx.restore();
    }

  };

  // ─── Рендер нейрона ───────────────────────────────────────────────────────
  const drawJunction = (ctx: CanvasRenderingContext2D, j: Junction, camX: number, rgb: string, t: number) => {
    const sx = j.x - camX;
    if (sx < -60 || sx > g.current.w + 60) return;

    const glR = j.r + 14;
    const gl = ctx.createRadialGradient(sx, j.y, j.r * 0.2, sx, j.y, glR);
    gl.addColorStop(0, `rgba(${rgb},0.22)`);
    gl.addColorStop(0.6, `rgba(${rgb},0.06)`);
    gl.addColorStop(1, `rgba(${rgb},0)`);
    ctx.beginPath(); ctx.arc(sx, j.y, glR, 0, Math.PI * 2); ctx.fillStyle = gl; ctx.fill();

    ctx.beginPath(); ctx.arc(sx, j.y, j.r, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${rgb},0.06)`; ctx.fill();
    ctx.strokeStyle = `rgba(${rgb},0.3)`; ctx.lineWidth = 1.5; ctx.stroke();

    ctx.beginPath(); ctx.arc(sx, j.y, j.r * 0.35, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${rgb},0.12)`; ctx.fill();
    ctx.strokeStyle = `rgba(${rgb},0.18)`; ctx.lineWidth = 0.8; ctx.stroke();

    ctx.beginPath(); ctx.arc(sx + 2, j.y - 1, j.r * 0.12, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${rgb},0.22)`; ctx.fill();

    // Энергия в нейроне: свечение + молния в центре
    if (j.hasEnergy && !j.energyTaken) {
      const pulse = 0.7 + 0.3 * Math.sin(t * 0.006 + j.x * 0.01);
      const eGl = ctx.createRadialGradient(sx, j.y, 2, sx, j.y, j.r * 0.9);
      eGl.addColorStop(0, `rgba(250,204,21,${(0.35 * pulse).toFixed(2)})`);
      eGl.addColorStop(1, 'rgba(250,204,21,0)');
      ctx.beginPath(); ctx.arc(sx, j.y, j.r * 0.9, 0, Math.PI * 2); ctx.fillStyle = eGl; ctx.fill();

      ctx.strokeStyle = `rgba(250,204,21,${(0.8 * pulse).toFixed(2)})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(sx - 3, j.y - 7); ctx.lineTo(sx + 1, j.y - 2);
      ctx.lineTo(sx - 1, j.y + 1); ctx.lineTo(sx + 3, j.y + 7);
      ctx.stroke();
    }

    for (const b of j.bonuses) {
      if (b.collected) continue;
      const bx = sx + b.dx, by = j.y + b.dy;
      if (b.kind === 'data') {
        ctx.save(); ctx.translate(bx, by); ctx.rotate(Math.PI / 4 + t * 0.002);
        ctx.fillStyle = 'rgba(74,222,128,0.75)'; ctx.fillRect(-4, -4, 8, 8); ctx.restore();
      } else {
        ctx.beginPath(); ctx.arc(bx, by, 5, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(96,165,250,0.75)'; ctx.fill();
      }
    }
  };

  // ─── Игровой цикл ────────────────────────────────────────────────────────
  useEffect(() => {
    let lastT = performance.now();

    const loop = (t: number) => {
      const s = g.current;
      const cv = canvasRef.current;
      if (!cv) return;
      const ctx = cv.getContext('2d');
      if (!ctx) return;
      const dt = Math.min(3, (t - lastT) / 16);
      lastT = t;
      const { w, h } = s;
      const stInfo = STAGES[s.stageIdx];
      const screenPX = w * 0.22;

      if (s.state === 'playing') {
        const elapsed = (t - s.startAt) / 1000;
        // На первом уровне скорость ниже, дальше растёт по стадии и времени
        s.speed = BASE_SPEED + s.stageIdx * 0.35 + elapsed * 0.035;
        s.camX += s.speed * dt;
        s.playerWorldX = s.camX + screenPX;

        s.score = Math.floor((t - s.startAt) / 100) + s.bonus;
        s.stageIdx = getStage(s.score);
        ensureNetwork();

        // Ввод
        const inp = inputRef.current;
        const spd = 5.5 * dt;
        if (inp.active && inp.pointerY >= 0) s.targetY = inp.pointerY;
        if (inp.keysUp) s.targetY -= spd * 3;
        if (inp.keysDown) s.targetY += spd * 3;
        s.targetY = clamp(s.targetY, PLAYER_R + 5, h - PLAYER_R - 5);
        s.playerY += (s.targetY - s.playerY) * 0.2 * dt;

        // ── Зонная физика: сигнал внутри сети ──
        const pwx = s.playerWorldX;
        type Zone = { lo: number; hi: number; kind: 'j' | 'p'; idx: number };
        const zones: Zone[] = [];

        for (let i = 0; i < s.junctions.length; i++) {
          const j = s.junctions[i];
          const dx = pwx - j.x;
          if (Math.abs(dx) >= j.r) continue;
          const half = Math.sqrt(j.r * j.r - dx * dx);
          zones.push({ lo: j.y - half + PLAYER_R, hi: j.y + half - PLAYER_R, kind: 'j', idx: i });
        }

        for (let i = 0; i < s.pathways.length; i++) {
          const p = s.pathways[i];
          if (pwx < p.fromX || pwx > p.toX) continue;
          const pt = pathProgress(p, pwx);
          const cy = pathYAt(p, pt);
          const hw = hwAt(p.hw, pt);
          zones.push({ lo: cy - hw + PLAYER_R, hi: cy + hw - PLAYER_R, kind: 'p', idx: i });
        }

        if (zones.length > 0) {
          let best = zones[0], bestD = Infinity;
          for (const z of zones) {
            const d = s.playerY < z.lo ? z.lo - s.playerY : s.playerY > z.hi ? s.playerY - z.hi : 0;
            if (d < bestD) { bestD = d; best = z; }
          }

          s.playerY = clamp(s.playerY, best.lo, best.hi);
          s.targetY = clamp(s.targetY, best.lo, best.hi);

          if (best.kind === 'j') {
            s.atJunction = best.idx;
            s.currentPathway = -1;
            const j = s.junctions[best.idx];
            // Энергию в нейроне нельзя пропустить: вошли в нейрон -> сразу получили
            if (j.hasEnergy && !j.energyTaken) {
              j.energyTaken = true;
              if (s.energy < MAX_ENERGY) {
                s.energy += 1;
                spawnExplosion(screenPX, s.playerY, '#facc15');
              } else {
                s.bonus += 15;
                spawnExplosion(screenPX, s.playerY, '#facc15');
              }
            }
            // Сбор бонусов внутри нейрона (очки)
            for (const b of j.bonuses) {
              if (b.collected) continue;
              const bwx = j.x + b.dx, bwy = j.y + b.dy;
              if (Math.abs(pwx - bwx) < PLAYER_R + 10 && Math.abs(s.playerY - bwy) < PLAYER_R + 10) {
                b.collected = true;
                s.bonus += b.kind === 'weight' ? 25 : 10;
                spawnExplosion(screenPX, s.playerY, b.kind === 'weight' ? '#60a5fa' : '#4ade80');
              }
            }
          } else {
            s.currentPathway = best.idx;
            s.atJunction = -1;
            const p = s.pathways[best.idx];
            const pt = pathProgress(p, pwx);

            // ── Синаптический затвор (t≈0.5) ──
            if (!p.gateOpen && pt >= 0.45 && pt <= 0.55) {
              if (s.energy >= p.gateCost) {
                s.energy -= p.gateCost;
                p.gateOpen = true;
                p.openedAt = t; // для вспышки
                spawnExplosion(screenPX, s.playerY, GATE_COLORS[p.gateCost]);
                // Эффект выброса нейромедиаторов
                for (let ei = 0; ei < 8; ei++) {
                  const ea = Math.random() * Math.PI * 2;
                  s.particles.push({ x: screenPX, y: s.playerY, vx: Math.cos(ea) * (2 + Math.random() * 3), vy: Math.sin(ea) * (2 + Math.random() * 3), life: 1.2, color: GATE_COLORS[p.gateCost], r: 1.5 + Math.random() * 2 });
                }
              } else {
                spawnExplosion(screenPX, s.playerY, '#ef4444');
                endGame(s.score);
              }
            }

            // Сбор бонусов за очки
            if (p.dataT >= 0 && !p.collected) {
              const dx = pathXAt(p, p.dataT), dy = pathYAt(p, p.dataT);
              if (Math.abs(pwx - dx) < PLAYER_R + 12 && Math.abs(s.playerY - dy) < PLAYER_R + 12) {
                p.collected = true;
                s.bonus += p.dataKind === 'weight' ? 25 : 10;
                spawnExplosion(screenPX, s.playerY, p.dataKind === 'weight' ? '#60a5fa' : '#4ade80');
              }
            }
          }
        } else {
          let cj = 0, cjd = Infinity;
          for (let ji = 0; ji < s.junctions.length; ji++) {
            const dd = Math.abs(s.junctions[ji].x - pwx);
            if (dd < cjd) { cjd = dd; cj = ji; }
          }
          s.playerY = s.junctions[cj].y;
          s.targetY = s.playerY;
          s.atJunction = cj;
        }

        s.trail.push({ x: screenPX, y: s.playerY });
        if (s.trail.length > TRAIL_LEN) s.trail.splice(0, s.trail.length - TRAIL_LEN);

        for (const p of s.particles) { p.x += p.vx * dt; p.y += p.vy * dt; p.life -= 0.03 * dt; }
        s.particles = s.particles.filter(p => p.life > 0);

        setDisplayScore(s.score);
        setDisplayStage(s.stageIdx);
        setDisplayEnergy(s.energy);
      }

      // ── Рендер ──
      ctx.clearRect(0, 0, w, h);

      const bg = ctx.createLinearGradient(0, 0, 0, h);
      bg.addColorStop(0, '#030712'); bg.addColorStop(1, '#0a0f1e');
      ctx.fillStyle = bg; ctx.fillRect(0, 0, w, h);

      ctx.strokeStyle = 'rgba(100,116,139,0.03)'; ctx.lineWidth = 0.5;
      const gs = 36, go = -(s.camX % gs);
      for (let gx = go; gx < w; gx += gs) { ctx.beginPath(); ctx.moveTo(gx, 0); ctx.lineTo(gx, h); ctx.stroke(); }
      for (let gy = 0; gy < h; gy += gs) { ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(w, gy); ctx.stroke(); }

      for (const p of s.pathways) drawPathway(ctx, p, s.camX, stInfo.accent, t);

      const rgb = hexRgb(stInfo.accent);
      for (const j of s.junctions) drawJunction(ctx, j, s.camX, rgb, t);

      if (s.state === 'playing' || s.state === 'over') {
        // Трейл
        for (let i = 0; i < s.trail.length; i++) {
          const tp = s.trail[i]; const a = (i / s.trail.length) * 0.3; const r = PLAYER_R * (i / s.trail.length) * 0.5;
          ctx.beginPath(); ctx.arc(tp.x, tp.y, r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${rgb},${a})`; ctx.fill();
        }

        // ── Сигнал: цвет зависит от уровня энергии ──
        const energy = s.energy;
        const eColor = ENERGY_COLORS[energy] || ENERGY_COLORS[0];
        const eRgb = hexRgb(eColor);
        const baseGlow = PLAYER_R + 3 + energy * 2 + Math.sin(t * 0.004) * 1;

        // Кольца энергии (цвет по уровню)
        for (let ring = 0; ring < energy; ring++) {
          const phase = t * 0.003 + ring * 2.1;
          const rr = PLAYER_R + 3 + ring * 4 + Math.sin(phase) * 1.5;
          const alpha = 0.18 + 0.12 * Math.sin(phase);
          ctx.beginPath(); ctx.arc(screenPX, s.playerY, rr, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(${eRgb},${alpha})`;
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }

        // Основное свечение (цвет энергии)
        const gl = ctx.createRadialGradient(screenPX, s.playerY, PLAYER_R * 0.2, screenPX, s.playerY, baseGlow);
        const glowAlpha = 0.25 + energy * 0.06;
        gl.addColorStop(0, `rgba(${eRgb},${glowAlpha})`); gl.addColorStop(1, `rgba(${eRgb},0)`);
        ctx.beginPath(); ctx.arc(screenPX, s.playerY, baseGlow, 0, Math.PI * 2); ctx.fillStyle = gl; ctx.fill();

        // Ядро сигнала (цвет = уровень энергии)
        ctx.beginPath(); ctx.arc(screenPX, s.playerY, PLAYER_R, 0, Math.PI * 2);
        ctx.fillStyle = eColor; ctx.fill();
        ctx.beginPath(); ctx.arc(screenPX - 0.8, s.playerY - 0.8, PLAYER_R * 0.3, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.5)'; ctx.fill();

        // Частицы
        for (const p of s.particles) {
          ctx.globalAlpha = Math.max(0, p.life);
          ctx.beginPath(); ctx.arc(p.x, p.y, p.r * p.life, 0, Math.PI * 2);
          ctx.fillStyle = p.color; ctx.fill();
        }
        ctx.globalAlpha = 1;
      }

      if (s.state === 'menu') {
        s.camX += 0.3 * dt;
        ensureNetwork();
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    const s = g.current;
    if (s.junctions.length === 0) {
      s.junctions = [{ x: 0, y: s.h / 2, r: JUNCTION_R, bonuses: [], hasEnergy: false, energyTaken: false }];
      s.frontier = [0];
      ensureNetwork();
    }

    rafRef.current = requestAnimationFrame(loop);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [gameState, endGame]);

  const spawnExplosion = (x: number, y: number, color: string) => {
    for (let i = 0; i < 14; i++) {
      const a = Math.random() * Math.PI * 2;
      g.current.particles.push({ x, y, vx: Math.cos(a) * (1.5 + Math.random() * 2), vy: Math.sin(a) * (1.5 + Math.random() * 2), life: 1, color, r: 2 + Math.random() * 2.5 });
    }
  };

  const getStage = (score: number) => { for (let i = STAGES.length - 1; i >= 0; i--) if (score >= STAGES[i].threshold) return i; return 0; };

  return (
    <div ref={containerRef} className="relative w-full h-full select-none overflow-hidden" style={{ touchAction: 'none' }}>
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

      {/* HUD */}
      {gameState === 'playing' && (
        <div className="absolute top-0 inset-x-0 flex items-center justify-between px-4 py-2 pointer-events-none z-10">
          <div className="flex items-center gap-3">
            <div className="px-3 py-1 rounded-xl text-xs font-bold uppercase tracking-wider border" style={{ color: STAGES[displayStage].accent, borderColor: `${STAGES[displayStage].accent}40`, background: 'rgba(0,0,0,0.5)' }}>
              {STAGES[displayStage].name}
            </div>
            {displayStage < STAGES.length - 1 && (
              <div className="h-1.5 w-24 rounded-full bg-site-surface/80 overflow-hidden">
                <div className="h-full rounded-full transition-all duration-300" style={{
                  width: `${Math.min(100, ((displayScore - STAGES[displayStage].threshold) / (STAGES[Math.min(STAGES.length - 1, displayStage + 1)].threshold - STAGES[displayStage].threshold)) * 100)}%`,
                  background: STAGES[displayStage].accent,
                }} />
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Индикатор энергии: 4 кольца, цвет по уровню */}
            <div className="flex items-center gap-1 px-2 py-1 rounded-lg" style={{ background: 'rgba(0,0,0,0.5)' }}>
              {[1, 2, 3, 4].map(lvl => {
                const ec = ENERGY_COLORS[displayEnergy] || ENERGY_COLORS[0];
                const on = displayEnergy >= lvl;
                return (
                  <div key={lvl} className="rounded-full border-2 transition-all duration-200" style={{
                    width: 14, height: 14,
                    borderColor: on ? ec : 'rgba(107,114,128,0.3)',
                    background: on ? `${ec}80` : 'transparent',
                    boxShadow: on ? `0 0 6px ${ec}66` : 'none',
                  }} />
                );
              })}
            </div>
            <div className="px-3 py-1 rounded-xl bg-black/50 text-site-text font-mono text-lg tabular-nums">{displayScore}</div>
          </div>
        </div>
      )}

      {/* Меню */}
      {gameState === 'menu' && (
        <div className="absolute inset-0 z-20 flex items-center justify-center">
          <div className="max-w-md w-full mx-4 rounded-2xl border border-site-border/50 bg-site-bg/80 backdrop-blur-lg p-6 text-center">
            <div className="text-3xl font-bold text-site-text mb-1">Neural Runner</div>
            <p className="text-site-muted text-sm mb-6">Проведи сигнал через нейросеть.<br/>Собирай энергию ⚡ чтобы открывать закрытые синапсы!</p>
            <button className="w-full py-3 rounded-xl bg-site-accent/90 hover:bg-site-accent text-site-on-accent font-semibold text-lg transition mb-3" onClick={initGame}>Начать</button>
            {onExit && <button className="w-full py-2.5 rounded-xl bg-site-surface/60 hover:bg-site-surface text-site-secondary transition text-sm" onClick={onExit}>Назад</button>}
            <div className="mt-4 text-xs text-site-muted">
              {typeof window !== 'undefined' && 'ontouchstart' in window ? 'Касание + перетаскивание' : '↑↓ / мышь · Пробел — старт'}
            </div>
            {leaderboard.length > 0 && (
              <div className="mt-5 border-t border-site-border/40 pt-4">
                <div className="text-xs text-site-muted mb-2 uppercase tracking-wider">Топ игроков</div>
                {leaderboard.slice(0, 5).map((p, i) => (
                  <div key={i} className="flex items-center gap-2 py-1 text-sm">
                    <span className="w-5 text-site-muted text-right">{i + 1}.</span>
                    <span className="flex-1 text-left text-site-text truncate">{p.name}</span>
                    <span className="text-site-secondary font-mono">{p.score}</span>
                  </div>
                ))}
              </div>
            )}
            {(myBest > 0 || globalBest > 0) && (
              <div className="mt-3 flex justify-center gap-4 text-xs text-site-muted">
                {myBest > 0 && <span>Мой рекорд: <span className="text-site-text">{myBest}</span></span>}
                {globalBest > 0 && <span>Рекорд: <span className="text-site-text">{globalBest}</span></span>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Game Over */}
      {gameState === 'over' && (
        <div className="absolute inset-0 z-20 flex items-center justify-center">
          <div className="max-w-sm w-full mx-4 rounded-2xl border border-site-border/50 bg-site-bg/80 backdrop-blur-lg p-6 text-center">
            <div className="text-xl font-bold text-site-text mb-1">Сигнал потерян</div>
            <div className="text-site-muted text-sm mb-2">Стадия: <span style={{ color: STAGES[displayStage].accent }}>{STAGES[displayStage].name}</span></div>
            <div className="text-4xl font-bold text-site-text my-4 font-mono">{finalScore}</div>
            <div className="flex justify-center gap-4 text-xs text-site-muted mb-5">
              <span>Мой рекорд: <span className="text-site-text font-semibold">{bestScore}</span></span>
              <span>Рекорд: <span className="text-site-text font-semibold">{Math.max(globalBest, bestScore)}</span></span>
            </div>
            <div className="flex gap-2">
              <button className="flex-1 py-2.5 rounded-xl bg-site-accent/90 hover:bg-site-accent text-site-on-accent font-semibold transition" onClick={initGame}>Ещё раз</button>
              {onExit && <button className="flex-1 py-2.5 rounded-xl bg-site-surface/60 hover:bg-site-surface text-site-secondary transition" onClick={onExit}>Выйти</button>}
            </div>
            {leaderboard.length > 0 && (
              <div className="mt-4 border-t border-site-border/40 pt-3">
                <div className="text-xs text-site-muted mb-2 uppercase tracking-wider">Топ игроков</div>
                {leaderboard.slice(0, 5).map((p, i) => (
                  <div key={i} className="flex items-center gap-2 py-1 text-sm">
                    <span className="w-5 text-site-muted text-right">{i + 1}.</span>
                    <span className="flex-1 text-left text-site-text truncate">{p.name}</span>
                    <span className="text-site-secondary font-mono">{p.score}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
